import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, BookOpen, Eye, Plus } from 'lucide-react';

export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState({ pages: 0, posts: 0, published: 0 });
  const [recentPages, setRecentPages] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pagesRes, postsRes] = await Promise.all([
        fetch(`${API_URL}/api/pages`),
        fetch(`${API_URL}/api/posts`)
      ]);
      
      const pages = await pagesRes.json();
      const posts = await postsRes.json();
      
      setStats({
        pages: pages.length,
        posts: posts.length,
        published: [...pages, ...posts].filter(item => item.status === 'published').length
      });
      
      setRecentPages(pages.slice(0, 5));
      setRecentPosts(posts.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400">Welcome to KayTool CMS</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/pages/new">
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              New Page
            </Button>
          </Link>
          <Link to="/admin/posts/new">
            <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.pages}</p>
                <p className="text-sm text-slate-400">Total Pages</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-600/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.posts}</p>
                <p className="text-sm text-slate-400">Total Posts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                <Eye className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.published}</p>
                <p className="text-sm text-slate-400">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Pages */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Recent Pages</CardTitle>
              <Link to="/admin/pages" className="text-sm text-blue-400 hover:text-blue-300">
                View all
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {recentPages.length === 0 ? (
              <p className="p-4 text-slate-400 text-center">No pages yet</p>
            ) : (
              <ul className="divide-y divide-slate-700">
                {recentPages.map((page) => (
                  <li key={page.id}>
                    <Link
                      to={`/admin/pages/${page.id}`}
                      className="flex items-center justify-between p-4 hover:bg-slate-700/50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-white">{page.title}</p>
                        <p className="text-sm text-slate-400">/{page.slug}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        page.status === 'published' 
                          ? 'bg-emerald-600/20 text-emerald-400' 
                          : 'bg-yellow-600/20 text-yellow-400'
                      }`}>
                        {page.status}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Recent Posts */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">Recent Posts</CardTitle>
              <Link to="/admin/posts" className="text-sm text-blue-400 hover:text-blue-300">
                View all
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {recentPosts.length === 0 ? (
              <p className="p-4 text-slate-400 text-center">No posts yet</p>
            ) : (
              <ul className="divide-y divide-slate-700">
                {recentPosts.map((post) => (
                  <li key={post.id}>
                    <Link
                      to={`/admin/posts/${post.id}`}
                      className="flex items-center justify-between p-4 hover:bg-slate-700/50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-white">{post.title}</p>
                        <p className="text-sm text-slate-400">/blog/{post.slug}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        post.status === 'published' 
                          ? 'bg-emerald-600/20 text-emerald-400' 
                          : 'bg-yellow-600/20 text-yellow-400'
                      }`}>
                        {post.status}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Quick Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-center transition-colors"
            >
              <p className="font-medium text-white">View Site</p>
              <p className="text-sm text-slate-400">Main compressor</p>
            </a>
            <a
              href="/quick-50kb"
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-center transition-colors"
            >
              <p className="font-medium text-white">Quick 50KB</p>
              <p className="text-sm text-slate-400">Quick tool</p>
            </a>
            <Link
              to="/admin/menus"
              className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-center transition-colors"
            >
              <p className="font-medium text-white">Menus</p>
              <p className="text-sm text-slate-400">Edit navigation</p>
            </Link>
            <Link
              to="/admin/settings"
              className="p-4 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-center transition-colors"
            >
              <p className="font-medium text-white">Settings</p>
              <p className="text-sm text-slate-400">Site config</p>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
